import React, { useState, useContext } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Autocomplete,
  CircularProgress,
  Alert,
  Paper,
  Divider,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import { LoadToCosmos } from './SendToCosmos';
import { ModeContext } from './ModeContext';

// グループ選択肢の定義（表示名と、検索条件に使う表記ゆれ値の配列）
const GROUP_OPTIONS = [
  { value: '', label: 'すべて', matches: [] },
  {
    value: 'sp',
    label: 'サービスポータル事業部',
    matches: ['ｻｰﾋﾞｽﾎﾟｰﾀﾙ事業部', 'サービスポータル事業部（共通）'],
  },
  {
    value: 'hospitality',
    label: 'ホスピタリティＳ事業部',
    matches: ['ホスピタリティＳ事業部（共通）'],
  },
  {
    value: 'logistics',
    label: 'ロジスティックス',
    matches: ['ロジスティクスＳ事業部(共通)', 'ロジスティックスＳ事業部(共通)'],
  },
  {
    value: 'retail',
    label: 'リテイル事業部',
    matches: ['ﾘﾃｲﾙｲﾉﾍﾞｰｼｮﾝ事業部', 'リテイルイノベーション事業部（共）', 'リテイル事業部（共通）'],
  },
  {
    value: 'food',
    label: 'フード事業部',
    matches: ['フードインダストリーＳ事業部（共）', 'フードインダストリーＳ事業部（共通）'],
  },
  {
    value: 'digijapan',
    label: 'デジジャパン',
    matches: ['デジジャパン'],
  },
  {
    value: 'packaging',
    label: '包装事業部',
    matches: ['包装事業部（共通）', '包装環境事業部', '包装環境事業部（共通）'],
  },
  {
    value: 'public',
    label: '公共ソリューション',
    matches: ['公共ｿﾘｭｰｼｮﾝﾌﾟﾛｼﾞｪｸﾄ'],
  },
  {
    value: 'payment',
    label: 'ペイメント事業部',
    matches: ['ﾍﾟｲﾒﾝﾄﾎﾟｰﾀﾙ事業部'],
  },
  {
    value: 'scale',
    label: 'スケールメディア',
    matches: ['ｽｹｰﾙ・ﾒﾃﾞｨｱｿﾘｭｰｼｮﾝ事業部（共通）'],
  },
  {
    value: 'environment',
    label: '環境事業部',
    matches: ['環境事業部（共通）'],
  },
];

// 日付をyyyy-MM-dd形式にフォーマット
const formatDate = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// 今日の日付をデフォルト値として取得
const getToday = () => formatDate(new Date());

// 1ヶ月前の日付をデフォルト値として取得
const getOneMonthAgo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return formatDate(d);
};

// タイムスタンプを見やすい日時文字列に変換
const formatTimestamp = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' })
    + ' ' + d.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

// CSVエスケープ処理（ダブルクォート、改行対応）
const escapeCsvField = (field) => {
  if (field === null || field === undefined) return '';
  const str = String(field);
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return '"' + str.replace(/"/g, '""') + '"';
  }
  return str;
};

const SettingsHistoryDownload = () => {
  const { loginInfo } = useContext(ModeContext);
  const [dateFrom, setDateFrom] = useState(getOneMonthAgo());
  const [dateTo, setDateTo] = useState(getToday());
  const [filterUser, setFilterUser] = useState('');
  const [filterGroup, setFilterGroup] = useState(null);
  const [filterGroupInput, setFilterGroupInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');

  // HQまたはstoreユーザーかどうか判定
  const isLimitedUser = loginInfo.authorityId === 'HQ' || loginInfo.authorityId === 'store';

  // CosmosDBからデータを検索
  const handleSearch = async () => {
    setError('');
    setPreviewData([]);
    setSearched(false);

    if (!dateFrom || !dateTo) {
      setError('開始日と終了日を指定してください。');
      return;
    }
    if (new Date(dateFrom) > new Date(dateTo)) {
      setError('開始日は終了日より前の日付を指定してください。');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        id: 'all',
        container: 'con-concierge-knowledge',
        dateFrom: new Date(dateFrom).getTime(),
        dateTo: new Date(dateTo + 'T23:59:59').getTime(),
        // HQ/storeはcompIdを自身のものに強制セット、adminは指定なし（全データ取得）
        compId: isLimitedUser ? loginInfo.compId : undefined,
      };

      const result = await LoadToCosmos(payload);
      let items = result?.item || result?.items || [];

      // 【最適化】フィルタリング順序：絞り込みが大きいものから先に実行
      
      // 1. HQ/storeの場合、compIdでフィルタリング（バックエンド未対応時の保険）
      if (isLimitedUser && loginInfo.compId) {
        items = items.filter((item) => item.compId === loginInfo.compId);
      }

      // 2. admin向け：user / グループ でフロントエンドフィルタリング（日付より先に絞り込む）
      if (!isLimitedUser) {
        if (filterUser.trim()) {
          const keyword = filterUser.trim().toLowerCase();
          items = items.filter((item) => (item.user || '').toLowerCase().includes(keyword));
        }
        if (filterGroup) {
          // 選択肢から選んだ場合：matches配列で完全一致フィルタ
          if (filterGroup.matches && filterGroup.matches.length > 0) {
            items = items.filter((item) =>
              filterGroup.matches.some((m) => item.groupId === m)
            );
          }
        } else if (filterGroupInput.trim()) {
          // 手入力の場合：部分一致フィルタ
          const keyword = filterGroupInput.trim().toLowerCase();
          items = items.filter((item) => (item.groupId || '').toLowerCase().includes(keyword));
        }
      }

      // 3. 日付フィルタリング（絞り込まれたデータに対して実行）
      const fromTimestamp = new Date(dateFrom).getTime();
      const toTimestamp = new Date(dateTo + 'T23:59:59').getTime();
      items = items.filter((item) => {
        const itemTimestamp = item.timestamp;
        return itemTimestamp >= fromTimestamp && itemTimestamp <= toTimestamp;
      });

      if (Array.isArray(items) && items.length > 0) {
        setPreviewData(items);
      } else {
        setPreviewData([]);
      }
      setSearched(true);
    } catch (err) {
      console.error('検索エラー:', err);
      setError('データの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  // CSVファイルを生成してダウンロード
  const handleDownload = () => {
    if (previewData.length === 0) return;

    // CSVヘッダー定義
    const headers = [
      '日時',
      '顧客ID',
      '顧客名',
      'ユーザー',
      'モード',
      'グループID',
      '質問',
      '回答',
      'フィードバック',
    ];

    // CSVデータ行を生成
    const rows = previewData.map((item) => [
      escapeCsvField(formatTimestamp(item.timestamp)),
      escapeCsvField(item.compId),
      escapeCsvField(item.compName),
      escapeCsvField(item.user),
      escapeCsvField(item.mode),
      escapeCsvField(item.groupId),
      escapeCsvField(item.question),
      escapeCsvField(item.answer),
      escapeCsvField(item.feedback),
    ]);

    // BOM付きUTF-8でCSV文字列を作成（Excelで文字化けしないように）
    const bom = '\uFEFF';
    const csvContent = bom + [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');

    // ダウンロード処理
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `質問履歴_${dateFrom}_${dateTo}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ height: '80vh', overflow: 'auto', p: 2 }}>
      <Typography variant="h5" component="div" sx={{ color: '#1976d2', mb: 2 }}>
        <HistoryIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
        質問履歴ダウンロード
      </Typography>

      {/* 検索条件 */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
          検索条件
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="開始日"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ minWidth: 160 }}
          />
          <Typography sx={{ alignSelf: 'center' }}>〜</Typography>
          <TextField
            label="終了日"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ minWidth: 160 }}
          />
          {/* admin向け：user / グループ フィルター */}
          {!isLimitedUser && (
            <>
              <TextField
                label="ユーザー"
                value={filterUser}
                onChange={(e) => setFilterUser(e.target.value)}
                size="small"
                sx={{ minWidth: 140 }}
                placeholder="部分一致"
              />
              <Autocomplete
                freeSolo
                options={GROUP_OPTIONS.filter((opt) => opt.value !== '')}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
                value={filterGroup}
                onChange={(e, newValue) => setFilterGroup(newValue)}
                inputValue={filterGroupInput}
                onInputChange={(e, newInputValue) => setFilterGroupInput(newInputValue)}
                size="small"
                sx={{ minWidth: 220 }}
                renderInput={(params) => (
                  <TextField {...params} label="グループ" placeholder="選択または入力" />
                )}
                isOptionEqualToValue={(option, value) => option.value === value?.value}
              />
            </>
          )}
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleSearch}
            disabled={loading}
            sx={{ height: 40 }}
          >
            検索
          </Button>
        </Box>
      </Paper>

      {/* エラー表示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* ローディング */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* 検索結果 */}
      {searched && !loading && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              検索結果: {previewData.length} 件
            </Typography>
            <Button
              variant="contained"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              disabled={previewData.length === 0}
            >
              CSVダウンロード
            </Button>
          </Box>
          <Divider sx={{ mb: 1 }} />

          {previewData.length === 0 ? (
            <Typography sx={{ py: 2, textAlign: 'center', color: 'text.secondary' }}>
              該当するデータがありません。
            </Typography>
          ) : (
            <Box sx={{ overflow: 'auto', maxHeight: '50vh' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th style={thStyle}>日時</th>
                    <th style={thStyle}>顧客ID</th>
                    <th style={thStyle}>顧客名</th>
                    <th style={thStyle}>ユーザー</th>
                    <th style={thStyle}>グループ</th>
                    <th style={thStyle}>モード</th>
                    <th style={thStyle}>質問</th>
                    <th style={thStyle}>回答</th>
                    <th style={thStyle}>フィードバック</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.slice(0, 100).map((item, index) => (
                    <tr key={item.id || index} style={{ borderBottom: '1px solid #e0e0e0' }}>
                      <td style={tdStyle}>{formatTimestamp(item.timestamp)}</td>
                      <td style={tdStyle}>{item.compId}</td>
                      <td style={tdStyle}>{item.compName}</td>
                      <td style={tdStyle}>{item.user}</td>
                      <td style={tdStyle}>{item.groupId}</td>
                      <td style={tdStyle}>{item.mode}</td>
                      <td style={{ ...tdStyle, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.question}
                      </td>
                      <td style={{ ...tdStyle, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.answer}
                      </td>
                      <td style={tdStyle}>{item.feedback}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {previewData.length > 100 && (
                <Typography sx={{ mt: 1, color: 'text.secondary', fontSize: '0.8rem' }}>
                  ※ プレビューは先頭100件のみ表示しています。CSVダウンロードには全{previewData.length}件が含まれます。
                </Typography>
              )}
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

// テーブルスタイル
const thStyle = {
  padding: '8px 6px',
  textAlign: 'left',
  borderBottom: '2px solid #1976d2',
  whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '6px',
  textAlign: 'left',
  verticalAlign: 'top',
};

export default SettingsHistoryDownload;
